const ODOO_BASE = 'https://www.siliconesplus.com'

/**
 * Build an Odoo web-client deep link. Matches the pattern used by the
 * current reports SPA: `/web#id={id}&model={model}&view_type=form`.
 *
 * Memory note `reference_odoo_url_format`: self-hosted at
 * www.siliconesplus.com, use `/web#id=…&model=…`, NOT the `/odoo/` SaaS
 * path.
 */
export function odooUrl(model: string, id: number | string): string {
  return `${ODOO_BASE}/web#id=${id}&model=${encodeURIComponent(model)}&view_type=form`
}

/**
 * Open the product *template* form in Odoo. From the template form, the
 * "Forecasted" smart button navigates to the stock-forecast report with
 * proper context (in-app navigation populates context via `doAction()`,
 * which works correctly).
 *
 * Why not link straight to the forecast report: Odoo 17's hash router
 * does NOT populate `props.action.context` for the
 * `stock.stock_forecasted_product_product_action` client action when
 * the URL is loaded fresh (new tab / external link). The
 * StockForecasted Owl component then crashes its `setup()` reading
 * `useState(undefined).active_id`. Tested patterns that all crashed
 * identically: `action=stock.stock_forecasted_…&active_id=…`,
 * `action=stock_forecasted&active_id=…` (bare client-action tag),
 * `action=989&active_id=…&cids=1&menu_id=641` (the exact URL Odoo's
 * own smart button produces — works inside a session, crashes on
 * first-load), and JSON-encoded `&context={…}` variants.
 *
 * The template form pattern (`id=…&model=product.template&view_type=form`)
 * is reliable. User clicks Forecasted on the form to reach the same
 * report.
 */
export function odooProductTemplateUrl(productTemplateId: number | string): string {
  const id = encodeURIComponent(String(productTemplateId))
  return `${ODOO_BASE}/web#id=${id}&model=product.template&view_type=form`
}

/**
 * Deep link to a vendor's pricelist entries in Odoo's own native
 * "Vendor Pricelists" list (Purchase > Products > Vendor Pricelists),
 * domain-filtered to this vendor — NOT the vendor's `res.partner` form,
 * which has nothing pricing-related on it.
 *
 * `action=169` / `menu_id=433` confirmed live 2026-08-11 via
 * `ir.actions.act_window` + `ir.ui.menu` lookup (name: "Vendor
 * Pricelists", res_model: product.supplierinfo, no fixed domain — safe
 * to add our own). Mirrors `maildeskMessageUrl`'s action+menu shape,
 * which is the reliable pattern for this Odoo instance (plain
 * `model=…&view_type=list` with no action renders unstyled/unfiltered).
 */
export function odooVendorSupplierinfoUrl(vendorId: number | string): string {
  const domain = encodeURIComponent(JSON.stringify([['partner_id', '=', Number(vendorId)]]))
  return `${ODOO_BASE}/web#action=169&cids=1&menu_id=433&model=product.supplierinfo&view_type=list&domain=${domain}`
}

/**
 * Deep link to a MailDesk message in Odoo's threaded client UI.
 *
 * Memory note `reference_maildesk_odoo_url`: action=1552 opens the threaded
 * MailDesk client keyed on the `maildesk.message_index` id. The
 * `model=maildesk.message_index&view_type=form` URL is the WRONG target —
 * it lands on the raw record form, not the conversation view.
 */
export function maildeskMessageUrl(messageIndexId: number | string): string {
  return `${ODOO_BASE}/web#action=1552&message=${messageIndexId}&cids=1&menu_id=972`
}
